import { CaretLeftIcon, EyeIcon, FileTextIcon, PaperclipIcon, PlusIcon } from '@phosphor-icons/react';
import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { InputField } from 'components/input/InputField';
import { Formik } from 'components/form/formik';
import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';
import diff from 'object-diff';
import toast from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { invariant } from '@utils/invariant';
import * as Yup from 'yup';

import { getDisplayError } from '../../../utils/get-display-error';
import { formatDate, formatInputDate } from '../../../utils/date';
import { FileUploaderButton } from '../../document/components/FileUploaderButton';
import { useDownloadDocument } from '../../document/hooks/useDownloadDocument';
import { Pill } from '../../../components/pill/Pill';
import { captureException } from '@sentry/react';
import { useTranslation } from '../../../contexts/translation-context';
import {
  updateCarrierPortalTruck,
  linkCarrierPortalTruckDocument,
  updateCarrierPortalTruckAppPassword,
} from '../models';
import { TruckAppCredentialsCard } from '../../../components/truck/TruckAppCredentialsCard';
import { TruckTrackingPanel } from '../../telemetry/components/TruckTrackingPanel';
import { useAuth } from '../../../contexts/auth-context';
import {
  getTruckDocumentStatus,
  isMoreSevere,
  TRUCK_DOCUMENT_STATUS_COLOR,
  type TruckDocumentStatus,
} from './truck-document-status';

const updateTruckSchema = Yup.object().shape({
  licensePlate: Yup.string().required('errors.required'),
  insuranceExpiresAt: Yup.string().required('errors.required'),
  examinationExpiresAt: Yup.string().required('errors.required'),
});

const STATUS_LABEL_KEY = {
  valid: 'carrierPortal.trucks.statusValid',
  expiringSoon: 'carrierPortal.trucks.statusExpiringSoon',
  expired: 'carrierPortal.trucks.statusExpired',
  missing: 'carrierPortal.trucks.statusMissing',
} as const;

export const CarrierPortalUpdateTruckPage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { me } = useAuth();
  const { supplier, carrierId, refreshData } = useCarrierPortalSupplier();
  const { downloadFile, downloadingFiles } = useDownloadDocument();
  const isDispatcher = me?.carriers.some((c) => c.id === carrierId && c.role === 'CarrierDispatcher') ?? false;
  const { carrierTruckId } = useParams<{ carrierTruckId: string }>();
  invariant(carrierTruckId);

  const truck = supplier.trucks.find((c) => c.id === carrierTruckId);

  if (!truck) {
    return <div className="text-sm text-fg-muted">{i18n('carrierPortal.trucks.notFound')}</div>;
  }

  const initialValues = {
    licensePlate: truck.licensePlate,
    insuranceExpiresAt: formatInputDate(truck.insuranceExpiresAt),
    examinationExpiresAt: formatInputDate(truck.examinationExpiresAt),
  };

  const documents = [
    {
      type: 'Insurance' as const,
      label: i18n('carrierPortal.trucks.insurance'),
      expiresAt: truck.insuranceExpiresAt,
      file: truck.documents.find((d) => d.type === 'Insurance')?.document ?? null,
      uploadedMessage: i18n('carrierPortal.trucks.insuranceUploaded'),
    },
    {
      type: 'Examination' as const,
      label: i18n('carrierPortal.trucks.examination'),
      expiresAt: truck.examinationExpiresAt,
      file: truck.documents.find((d) => d.type === 'Examination')?.document ?? null,
      uploadedMessage: i18n('carrierPortal.trucks.examinationUploaded'),
    },
  ].map((doc) => ({ ...doc, status: getTruckDocumentStatus(doc.expiresAt) }));

  const uploadedCount = documents.filter((d) => d.file).length;

  /** The badge beside the licence plate calls out the document that needs attention first. */
  const worstDocument = documents.reduce<(typeof documents)[number] | null>((worst, doc) => {
    if (doc.status === 'valid') return worst;
    return worst === null || isMoreSevere(doc.status, worst.status) ? doc : worst;
  }, null);

  return (
    <div className="flex flex-col gap-4">
      <Link
        to=".."
        className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-fg-secondary transition-colors hover:text-fg"
      >
        <CaretLeftIcon className="h-3.5 w-3.5 text-fg-muted" />
        {i18n('carrierPortal.businessInformation.tabFleet')}
      </Link>

      <div className="flex flex-wrap items-center gap-2.5">
        <h1 className="heading-one">{truck.licensePlate}</h1>
        {worstDocument && (
          <Pill color={TRUCK_DOCUMENT_STATUS_COLOR[worstDocument.status]}>
            {`${worstDocument.label} · ${i18n(STATUS_LABEL_KEY[worstDocument.status])}`}
          </Pill>
        )}
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex flex-col gap-4">
          <Card title={i18n('carrierPortal.trucks.detailsTitle')}>
            <Formik
              initialValues={initialValues}
              validationSchema={updateTruckSchema}
              onSubmit={async (values) => {
                try {
                  const patch: Partial<typeof values> = diff(initialValues, values);
                  await updateCarrierPortalTruck(carrierId, carrierTruckId, patch);
                  navigate('..');
                  toast.success(i18n('carrierPortal.trucks.succeeded'));
                } catch (err: any) {
                  captureException(err);
                  toast.error(i18n('carrierPortal.trucks.updateError') + getDisplayError(err));
                }
              }}
            >
              {({ handleSubmit, isSubmitting }) => (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid gap-3.5 sm:grid-cols-3">
                    <InputField labelText={i18n('carrierPortal.trucks.licensePlate')} type="text" name="licensePlate" />
                    <InputField
                      labelText={i18n('carrierPortal.trucks.insuranceExpiresAt')}
                      type="date"
                      name="insuranceExpiresAt"
                    />
                    <InputField
                      labelText={i18n('carrierPortal.trucks.examinationExpiresAt')}
                      type="date"
                      name="examinationExpiresAt"
                    />
                  </div>

                  <div>
                    <Button
                      type="submit"
                      color="primary"
                      iconLeft={<PlusIcon className="button-icon" />}
                      isDisabled={isSubmitting}
                      isLoading={isSubmitting}
                    >
                      {i18n('carrierPortal.home.saveChanges')}
                    </Button>
                  </div>
                </form>
              )}
            </Formik>
          </Card>

          {isDispatcher ? (
            <TruckAppCredentialsCard
              title={i18n('carrierPortal.trucks.appLoginTitle')}
              loginHint={i18n('carrierPortal.trucks.appLoginHint')}
              licensePlate={truck.licensePlate}
              codeName={truck.name}
              onSavePassword={(password) =>
                updateCarrierPortalTruckAppPassword(carrierId, carrierTruckId, password).then(() => undefined)
              }
            />
          ) : null}

          <TruckTrackingPanel supplierTruckId={truck.id} party={{ carrierId }} />
        </div>

        <Card
          title={i18n('carrierPortal.trucks.documents')}
          rightHeading={
            <span className="card-subtle-text">
              {i18n('carrierPortal.trucks.documentCount', {
                count: `${uploadedCount}`,
                total: `${documents.length}`,
              })}
            </span>
          }
          flush
        >
          {documents.map((doc) => (
            <div className="flex flex-col gap-2.5 border-b border-border px-5 py-3.5 last:border-b-0" key={doc.type}>
              <div className="flex items-center gap-2">
                <FileTextIcon className="h-[15px] w-[15px] shrink-0 text-fg-muted" />
                <div className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">{doc.label}</div>
                <Pill color={TRUCK_DOCUMENT_STATUS_COLOR[doc.status as TruckDocumentStatus]}>
                  {i18n(STATUS_LABEL_KEY[doc.status])}
                </Pill>
              </div>

              {doc.expiresAt && (
                <div className="text-xs text-fg-secondary">
                  {i18n('carrierPortal.trucks.expiresOn', { date: formatDate(doc.expiresAt) })}
                </div>
              )}

              {doc.file ? (
                <div className="flex min-w-0 items-center gap-1.5">
                  <PaperclipIcon className="h-3.5 w-3.5 shrink-0 text-fg-muted" />
                  <span className="truncate text-2xs text-fg-muted">{doc.file.name}</span>
                </div>
              ) : (
                <div className="text-2xs text-fg-muted">{i18n('carrierPortal.trucks.noDocument')}</div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {doc.file && (
                  <Button
                    color="secondary"
                    size={8}
                    iconLeft={<EyeIcon className="button-icon" />}
                    isLoading={downloadingFiles.includes(doc.file.id)}
                    onTrigger={() => doc.file && downloadFile(doc.file)}
                  >
                    {i18n('carrierPortal.trucks.viewDocument')}
                  </Button>
                )}

                <FileUploaderButton
                  size={8}
                  buttonText={
                    doc.file
                      ? i18n('carrierPortal.trucks.replaceDocument')
                      : i18n('carrierPortal.trucks.uploadDocument')
                  }
                  title={doc.label}
                  initialName={`${doc.label} - ${truck.licensePlate}`}
                  onSubmit={async (document) => {
                    await linkCarrierPortalTruckDocument(carrierId, carrierTruckId, document.id, doc.type);
                    refreshData();
                    toast.success(doc.uploadedMessage);
                  }}
                />
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};
